import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Tag,
  Tabs,
  Tooltip,
  Popover,
  Button,
  Row,
  notification,
  Modal,
  DatePicker,
  Space,
  Select,
  Input,
} from "antd";
import { useLoading } from "../../context/loadingContext";
import dayjs from "dayjs";
import GetAdminInvoices from "../../functions/admin/getAdminInvoices";
import ExportAdminInvoices from "../../functions/admin/exportAdminInvoices";
import { downloadAdminInvoiceExcel } from "../../functions/admin/buildAdminInvoiceExcel";
import {
  invoiceControlRow,
  settleStateLabel,
} from "../../functions/admin/invoiceControlRow";
import GetMerchants from "../../functions/admin/getMerchants";
import GetPaymentMethods from "../../functions/reference/getPaymentMethods";
import GetProductTypes from "../../functions/reference/getProductType";
import axios from "axios";
import {
  CodeOutlined,
  LinkOutlined,
  RedoOutlined,
  FileExcelOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAdmin } from "../../context/adminContext";
import moneyFormat from "../../functions/utils/moneyFormat";
import AdminInvoiceAdd from "../../components/modal/adminInvoiceAdd";
import { bg_button, bg_shadow } from "constant/css";
import TablePagination from "components/pagination/tablePagination";
import ExpenseTransactions from "./expenseTransactions";

const { RangePicker } = DatePicker;

const moneyCell = (value) => (value == null ? "-" : moneyFormat(value));
const percentCell = (value) =>
  value == null ? "-" : Number(value).toFixed(2);

const activeValue = (value) =>
  value != null && value !== "" ? [String(value)] : null;

const asNumber = (value) => {
  const text = String(value ?? "").replace(/[\s,]/g, "");
  return text && Number.isFinite(Number(text)) ? text : null;
};

const filterIcon = (filtered) => (
  <SearchOutlined style={{ color: filtered ? "#13a38b" : undefined }} />
);

const AdminInvoice = () => {
  const { showLoading, hideLoading } = useLoading();
  const { searchFields, setSearchFields } = useAdmin();
  const [merchants, setMerchants] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [devMode, setDevMode] = useState(false);

  const [summary, setSummary] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [paginationData, setPaginationData] = useState({ pageSize: 20 });
  const [openModal, setOpenModal] = useState(false);
  const [excelModal, setExcelModal] = useState(false);
  const [excelRange, setExcelRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);
  const [excelExporting, setExcelExporting] = useState(false);
  const [controlTab, setControlTab] = useState("income");
  const [recipientDraft, setRecipientDraft] = useState(
    () => searchFields.recipient || ""
  );
  useEffect(() => {
    setRecipientDraft(searchFields.recipient || "");
  }, [searchFields.recipient]);
  const dateReady = useRef(false);
  const todayRange = () => [dayjs().startOf("day"), dayjs().endOf("day")];
  useEffect(() => {
    return () => {
      setSearchFields((prev) => {
        if (!prev?.createdAt && !prev?.recipient && !prev?.productTypeId)
          return prev;
        const next = { ...prev };
        delete next.createdAt;
        delete next.recipient;
        delete next.productTypeId;
        return next;
      });
    };
  }, [setSearchFields]);
  useEffect(() => {
    const hasDate = searchFields?.createdAt?.[0] && searchFields?.createdAt?.[1];
    if (!dateReady.current) {
      dateReady.current = true;
      if (!hasDate) {
        setSearchFields((prev) => ({
          ...prev,
          createdAt: todayRange(),
        }));
        return;
      }
    }
    prepareQueryStringAndSearch();
  }, [searchFields, page, pageSize]);
  const prepareQueryStringAndSearch = () => {
    const searchFieldsLength = Object.keys(searchFields).length;
    var queryString = "";
    if (searchFieldsLength !== 0) {
      queryString = Object.keys(searchFields)
        .filter((key) => searchFields[key] != null && searchFields[key] !== "")
        .map((key) => {
          if (key.includes("At")) {
            const start = dayjs(searchFields[key][0])
              .startOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            const end = dayjs(searchFields[key][1])
              .endOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            return `${key}=${encodeURIComponent(`${start}->${end}`)}`;
          }
          return `${key}=${encodeURIComponent(searchFields[key])}`;
        })
        .join("&");
    }
    fetchInvoices(`${queryString}${queryString ? "&" : ""}controlSummary=1`, page, pageSize);
  };
  const setFilter = (fieldName, value) => {
    setPage(1);
    setSearchFields((prev) => ({
      ...prev,
      [fieldName]: value || null,
    }));
  };
  const textFilter = (field, placeholder, normalize) => ({
    filteredValue: activeValue(searchFields[field]),
    filterIcon,
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div
        className="invoice-control-filter"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Input
          size="small"
          allowClear
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(event) =>
            setSelectedKeys(event.target.value ? [event.target.value] : [])
          }
          onPressEnter={(event) => {
            const raw = event.target.value;
            setFilter(
              field,
              normalize ? normalize(raw) : String(raw || "").trim()
            );
            confirm();
          }}
        />
        <Space style={{ marginTop: 8 }}>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              const raw = selectedKeys[0];
              setFilter(field, normalize ? normalize(raw) : String(raw || "").trim());
              confirm();
            }}
          >
            Хайх
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setFilter(field, null);
              confirm();
            }}
          >
            Цэвэрлэх
          </Button>
        </Space>
      </div>
    ),
  });
  const selectFilter = (field, options) => ({
    filteredValue: activeValue(searchFields[field]),
    filterIcon,
    filterDropdown: ({ confirm }) => (
      <div
        className="invoice-control-filter"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          placeholder="Сонгох"
          value={searchFields[field] || undefined}
          options={options}
          onChange={(value) => {
            setFilter(field, value);
            confirm();
          }}
        />
      </div>
    ),
  });
  const defaultColumns = [
    {
      title: "№",
      key: "no",
      width: 48,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Код",
      dataIndex: "id",
      key: "id",
      width: 80,
      ...textFilter("id", "Нэхэмжлэлийн код", asNumber),
    },
    {
      title: "Нэхэмжлэлийн дүн",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 130,
      render: (value) => moneyFormat(value),
      ...textFilter("amount", "Дүн", asNumber),
    },
    {
      title: "Шимтгэл",
      dataIndex: "feeAmount",
      key: "feeAmount",
      align: "right",
      width: 110,
      render: (value) => moneyCell(value),
    },
    {
      title: "Нийт дүн",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 120,
      render: (value) => moneyCell(value),
    },
    {
      title: "Төлбөрийн суваг",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 120,
      render: (value) => (value ? value.name : "-"),
      ...selectFilter(
        "paymentMethodId",
        paymentMethods
      ),
    },
    {
      title: "Шилжүүлэх данс",
      key: "fromAccountNumber",
      width: 140,
      render: (_, record) =>
        invoiceControlRow(record).fromAccountNumber || "-",
      ...textFilter("fromAccountNumber", "Дансны дугаар"),
    },
    {
      title: "Хүлээн авагч",
      key: "recipient",
      width: 180,
      render: (_, record) => {
        const recipients = record.recipients || [];
        if (!recipients.length) return "-";
        return recipients.map((recipient) => (
          <div key={recipient.account}>
            <div>{recipient.name || recipient.account}</div>
            {recipient.name ? (
              <div className="invoice-recipient-account">{recipient.account}</div>
            ) : null}
          </div>
        ));
      },
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      width: 120,
      align: "center",
      render: (value) =>
        value === "PAID" ? (
          <Tag color="cyan">Төлөгдсөн</Tag>
        ) : (
          <Tag>Төлөгдөөгүй</Tag>
        ),
    },
    {
      title: "Орлогын дүн",
      dataIndex: "bankIncomeAmount",
      key: "bankIncomeAmount",
      align: "right",
      width: 120,
      render: (value) => moneyCell(value),
    },
    {
      title: "Орлогийн төлөв",
      key: "bankIncomeStatus",
      width: 130,
      align: "center",
      render: (_, record) =>
        record.bankIncomeAmount != null ? (
          <Tag color="green">Орж ирсэн</Tag>
        ) : (
          <Tag>Орж ирээгүй</Tag>
        ),
    },
    {
      title: "Өндөрлөгөө",
      key: "merchantSettleId",
      width: 110,
      render: (_, record) => invoiceControlRow(record).settleIds || "-",
    },
    {
      title: "Өндөрлөгөөний төлөв",
      key: "settleState",
      width: 170,
      align: "center",
      render: (_, record) => {
        const row = invoiceControlRow(record);
        if (row.settleState === "MIXED") {
          return row.settleParts.map((part) => (
            <div key={`${part.id ?? "-"}-${part.state}`}>
              {part.id ?? "-"} {settleStateLabel(part.state)}
            </div>
          ));
        }
        const color =
          row.settleState === "SETTLED"
            ? "cyan"
            : row.settleState === "WAITING"
            ? "blue"
            : row.settleState === "FAILED"
            ? "red"
            : undefined;
        return <Tag color={color}>{settleStateLabel(row.settleState)}</Tag>;
      },
    },
    {
      title: "Гүйлгээний огноо",
      key: "occurredAt",
      width: 150,
      render: (_, record) => {
        const occurredAt = invoiceControlRow(record).occurredAt;
        return occurredAt ? dayjs(occurredAt).format("YYYY-MM-DD HH:mm") : "-";
      },
    },
    {
      title: "Merchant %",
      key: "merchantFeePercent",
      align: "right",
      width: 100,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).merchantFeePercent),
    },
    {
      title: "Мерчантын шимтгэл",
      key: "merchantFee",
      align: "right",
      width: 140,
      render: (_, record) => moneyCell(invoiceControlRow(record).merchantFee),
    },
    {
      title: "XPay %",
      key: "xpayFeePercent",
      align: "right",
      width: 90,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).xpayFeePercent),
    },
    {
      title: "XPay дүн",
      key: "xpayFee",
      align: "right",
      width: 110,
      render: (_, record) => moneyCell(invoiceControlRow(record).xpayFee),
    },
    {
      title: "SPay дүн",
      key: "spayFee",
      align: "right",
      width: 110,
      render: (_, record) => moneyCell(invoiceControlRow(record).spayFee),
    },
    {
      title: "Боловсруулалт %",
      key: "processingFeePercent",
      align: "right",
      width: 120,
      render: (_, record) =>
        percentCell(invoiceControlRow(record).processingFeePercent),
    },
    {
      title: "Боловсруулалтын дүн",
      key: "processingFee",
      align: "right",
      width: 150,
      render: (_, record) =>
        moneyCell(invoiceControlRow(record).processingFee),
    },
    {
      title: "Эцсийн дүн",
      key: "afterFeeAmount",
      align: "right",
      width: 130,
      render: (_, record) => (
        <span style={{ color: "#d46b08", fontWeight: 600 }}>
          {moneyCell(invoiceControlRow(record).afterFeeAmount)}
        </span>
      ),
    },
    {
      title: <span style={{ color: "red", fontSize: 10 }}>DEV BUTTONS!</span>,
      dataIndex: "action",
      key: "action",
      width: 100,
      render: (e, r) => (
        <div>
          <Button className="smallButton" onClick={() => forcePay(r)}>
            PAY
          </Button>
          <Button className="smallButton" onClick={() => forcePayLocal(r)}>
            PAY LOCAL
          </Button>
          <Button
            className="smallButton"
            onClick={() => openInvoice(r)}
            icon={<LinkOutlined />}
          ></Button>
          <Button
            className="smallButton"
            onClick={() => openInvoiceLocal(r)}
            icon={<LinkOutlined style={{ color: "red" }} />}
          ></Button>
        </div>
      ),
    },
  ];


  const invoiceItemColumns = [
    {
      title: "Нийт дүн",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "НӨАТ",
      dataIndex: "invoiceItemVatInfo",
      key: "invoiceItemVatInfo",
      render: (e) =>
        e?.status == "DONE" ? (
          <Popover title="VAT" content={<p>{JSON.stringify(e.qrJson)}</p>}>
            <Tag color="cyan">Үүссэн</Tag>
          </Popover>
        ) : (
          <Tag color="default">Үүссэгүй</Tag>
        ),
    },
    {
      title: (
        <Tooltip title="Мерчант-с өгсөн хаягыг төлөгдсөний дараа дуудсан статус">
          <span>Мэдээлэлсэн ?</span>
        </Tooltip>
      ),
      dataIndex: "callBackResponse",
      key: "callBackResponse",
      render: (e) =>
        e ? (
          String(e).includes("200") ? (
            <Tag color="cyan">200</Tag>
          ) : (
            <Tag color="default">{e}</Tag>
          )
        ) : (
          <Tag color="default">-</Tag>
        ),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm:ss") : "-"),
    },
    {
      title: "Бүт/төрөл",
      dataIndex: "productType",
      key: "productType",
      render: (e) => (e ? e.name : "-"),
    },
    {
      title: "Гүйлгээ",
      dataIndex: "transaction",
      key: "transaction",
      render: (e) => (e ? e.status : "-"),
    },
  ];
  const forcePay = async (record) => {
    var histories = record.qpayHistories;
    if (!histories || histories.length == 0) return;
    var sorted = histories.sort((a, b) => b.id - a.id);
    const qpayUrl = sorted[0].callBackUrl;
    if (!qpayUrl) return;
    axios
      .get(qpayUrl)
      .then((e) => {
        notification.success({
          message: "Success",
          description: "",
        });
      })
      .catch((e) => {
        if (e.response && e.response.data.error)
          notification.error({
            message: "Error",
            description: e.response.data.error.message,
          });
        else
          notification.error({
            message: "Error",
            description: "Failed to pay",
          });
      });
  };
  const forcePayLocal = async (record) => {
    var histories = record.qpayHistories;
    if (!histories || histories.length == 0)
      return notification.error({ message: "no history click link!" });
    var sorted = histories.sort((a, b) => b.id - a.id);
    var qpayUrl = sorted[0].callBackUrl;
    if (!qpayUrl)
      return notification.error({ message: "no history click link!" });
    qpayUrl = String(qpayUrl).replace(
      "https://apispay.esukh.mn",
      "http://localhost:8005"
    );
    axios
      .get(qpayUrl)
      .then((e) => {
        notification.success({
          message: "Success",
          description: "",
        });
      })
      .catch((e) => {
        if (e.response && e.response.data.error)
          notification.error({
            message: "Error",
            description: e.response.data.error.message,
          });
        else
          notification.error({
            message: "Error",
            description: "Failed to pay",
          });
      });
  };
  const openInvoice = async (record) => {
    window.open(`https://spay.esukh.mn/invoice?secureId=${record.secureId}`);
  };
  const openInvoiceLocal = async (record) => {
    window.open(`http://localhost:3000/invoice?secureId=${record.secureId}`);
  };
  useEffect(() => {
    refresh();
  }, []);
  const fetchInvoices = async (queryString, page, size) => {
    showLoading();
    const response = await GetAdminInvoices(
      `&page=${page}&size=${size}&${queryString}`
    );
    hideLoading();
    if (response) {
      setData(response.data);
      setPaginationData(response.pagination);
      setSummary(response.summary);
    }
  };
  const changePage = (page) => {
    setPage(page);
  };
  const addInvoice = (value) => {
    setOpenModal(true);
  };
  const closeModal = () => {
    setOpenModal(false);
  };
  const changeSize = (current, size) => {
    setPageSize(size);
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [merchantResponse, methods, types] = await Promise.all([
          GetMerchants(),
          GetPaymentMethods(),
          GetProductTypes(),
        ]);
        if (cancelled) return;
        setMerchants(
          (merchantResponse?.data || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
        setPaymentMethods(
          (methods || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
        setProductTypes(
          (types || []).map((row) => ({
            value: row.id,
            label: row.name,
          }))
        );
      } catch {
        if (!cancelled) {
          setMerchants([]);
          setPaymentMethods([]);
          setProductTypes([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const refresh = () => {
    setPage(1);
    setSearchFields({ createdAt: todayRange() });
  };
  const toggleDev = () => {
    setDevMode(!devMode);
  };
  const openExcelModal = () => {
    if (searchFields?.createdAt?.[0] && searchFields?.createdAt?.[1]) {
      setExcelRange([
        dayjs(searchFields.createdAt[0]),
        dayjs(searchFields.createdAt[1]),
      ]);
    }
    setExcelModal(true);
  };
  const handleExcelExport = async () => {
    if (!excelRange?.[0] || !excelRange?.[1]) {
      notification.warning({ message: "Эхлэх/дуусах огноо сонгоно уу" });
      return;
    }
    const startDate = excelRange[0].startOf("day").format("YYYY-MM-DD");
    const endDate = excelRange[1].endOf("day").format("YYYY-MM-DD");
    setExcelExporting(true);
    showLoading();
    try {
      const response = await ExportAdminInvoices({
        startDate,
        endDate,
        merchantId: searchFields.merchantId || undefined,
        status: searchFields.status || undefined,
        paymentMethodId: searchFields.paymentMethodId || undefined,
        callBackResponse: searchFields.callBackResponse || undefined,
        userId: searchFields.userId || undefined,
      });
      if (!response?.success || !response?.data) {
        notification.error({
          message: response?.message || "Excel өгөгдөл авахад алдаа гарлаа",
        });
        return;
      }
      downloadAdminInvoiceExcel(response.data);
      notification.success({
        message: "Excel татагдлаа",
        description: `${response.data.totalCount || 0} нэхэмжлэл · сар бүрийн sheet`,
      });
      setExcelModal(false);
    } catch (err) {
      notification.error({
        message:
          err.response?.data?.message || err.message || "Excel алдаа гарлаа",
      });
    } finally {
      setExcelExporting(false);
      hideLoading();
    }
  };
  return (
    <div className="vatMerchantListontainer mt-3">
      <Tabs
        activeKey={controlTab}
        onChange={setControlTab}
        items={[
          {
            key: "income",
            label: "Орлогын гүйлгээнүүд",
            children: (
              <>
      <Row style={{ gap: 10 }}>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            onClick={addInvoice}
          >
            Нэхэмжлэл үүсгэх
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            icon={<FileExcelOutlined />}
            onClick={openExcelModal}
          >
            Excel
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} ${bg_shadow} rounded-xl`}
            onClick={refresh}
          >
            <RedoOutlined />
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type={"primary"}
            className={`spayDefaultButton ${bg_button} ${bg_shadow} ${
              !devMode && "spayDefaultButtonInactive"
            }`}
            onClick={toggleDev}
          >
            <CodeOutlined />
          </Button>
        </div>
      </Row>
      <Row className="invoice-control-search" align="middle">
        <RangePicker
          size="small"
          value={searchFields.createdAt || null}
          onChange={(dates) =>
            setFilter(
              "createdAt",
              dates && dates[0] && dates[1] ? dates : null
            )
          }
          allowClear
          placeholder={["Гүйлгээний огноо", "Дуусах"]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бизнесийн нэр"
          style={{ width: 220 }}
          value={searchFields.merchantId || undefined}
          onChange={(value) => setFilter("merchantId", value)}
          options={merchants}
        />
        <Select
          size="small"
          allowClear
          placeholder="Төлөв"
          style={{ width: 150 }}
          value={searchFields.status || undefined}
          onChange={(value) => setFilter("status", value)}
          options={[
            { value: "PAID", label: "Төлөгдсөн" },
            { value: "NOT_PAID", label: "Төлөгдөөгүй" },
          ]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бүтээгдэхүүн"
          style={{ width: 180 }}
          value={searchFields.productTypeId || undefined}
          onChange={(value) => setFilter("productTypeId", value)}
          options={productTypes}
        />
        <Input.Search
          size="small"
          allowClear
          placeholder="Хүлээн авагч"
          style={{ width: 220 }}
          value={recipientDraft}
          onChange={(event) => {
            const value = event.target.value;
            setRecipientDraft(value);
            if (!value) setFilter("recipient", null);
          }}
          onSearch={(value) => setFilter("recipient", String(value || "").trim())}
        />
      </Row>
      <div className="tableContainer">
        <Table
          className="invoice-control-table"
          size="small"
          columns={defaultColumns.filter((e) => {
            if (e.key == "action") return devMode;
            return true;
          })}
          rowKey={(record) => record.id}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                className="invoice-control-table"
                columns={invoiceItemColumns}
                dataSource={record.invoiceItems}
                pagination={false}
              />
            ),
            expandIconColumnIndex: 0,
            rowExpandable: () => {
              return true;
            },
          }}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 38vh)",
          }}
          pagination={false}
          dataSource={data}
          summary={() => {
            const summaryMoney = (value) =>
              value == null || value === "" ? "-" : moneyFormat(value);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="summaryRow">
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1}>Нийт</Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    {summaryMoney(summary?.totalAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {summaryMoney(summary?.feeAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {summaryMoney(summary?.invoiceTotalAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} />
                  <Table.Summary.Cell index={8} />
                  <Table.Summary.Cell index={9} />
                  <Table.Summary.Cell index={10} align="right">
                    {summaryMoney(summary?.bankIncomeAmount)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} />
                  <Table.Summary.Cell index={12} />
                  <Table.Summary.Cell index={13} />
                  <Table.Summary.Cell index={14} />
                  <Table.Summary.Cell index={15} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={16} align="right">
                    {summaryMoney(summary?.merchantFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={17} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={18} align="right">
                    {summaryMoney(summary?.xpayFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={19} align="right">
                    {summaryMoney(summary?.spayFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={20} align="right">
                    -
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={21} align="right">
                    {summaryMoney(summary?.processingFee)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={22} align="right">
                    <span className="invoice-control-final">
                      {summaryMoney(summary?.afterFeeAmount)}
                    </span>
                  </Table.Summary.Cell>
                  {devMode ? <Table.Summary.Cell index={23} /> : null}
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        ></Table>
        <TablePagination
          paginationData={paginationData}
          page={page}
          changePage={changePage}
          changeSize={changeSize}
        />
      </div>
              </>
            ),
          },
          {
            key: "expense",
            label: "Зарлагын гүйлгээнүүд",
            children: (
              <ExpenseTransactions
                merchants={merchants}
                active={controlTab === "expense"}
              />
            ),
          },
        ]}
      />
      {openModal && (
        <AdminInvoiceAdd
          open={openModal}
          close={closeModal}
          refresh={refresh}
        />
      )}
      <Modal
        title="Нэхэмжлэл — Excel (нягтлан)"
        open={excelModal}
        onCancel={() => !excelExporting && setExcelModal(false)}
        okText="Татах"
        cancelText="Болих"
        confirmLoading={excelExporting}
        onOk={handleExcelExport}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div className="text-sm text-gray-600">
            Огнооны хязгаараар бүх нэхэмжлэлийг татна. Хүснэгтийн шүүлт
            (мерчант, төлөв, төлбөрийн хэлбэр гэх мэт) идэвхтэй бол хамт
            хэрэглэнэ.
          </div>
          <RangePicker
            style={{ width: "100%" }}
            value={excelRange}
            onChange={(v) => setExcelRange(v)}
            allowClear={false}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default AdminInvoice;
